import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createGang,
  updateGang,
  deleteGang,
  validateGangName,
  listGangs,
} from '../api/gangsApi';
import type {
  CreateGangRequest,
  UpdateGangRequest,
  ListGangsResponse,
} from '../api/gangsApi';

// Query keys for gangs
export const gangKeys = {
  all: ['gangs'] as const,
  lists: () => [...gangKeys.all, 'list'] as const,
  list: (filters: string) => [...gangKeys.lists(), { filters }] as const,
  details: () => [...gangKeys.all, 'detail'] as const,
  detail: (id: string) => [...gangKeys.details(), id] as const,
  validation: () => [...gangKeys.all, 'validation'] as const,
  validateName: (name: string) =>
    [...gangKeys.validation(), 'name', name] as const,
};

/**
 * Hook for validating gang names - uses query for caching
 */
export function useValidateGangName(
  name: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: gangKeys.validateName(name),
    queryFn: () => validateGangName(name),
    enabled: (options?.enabled ?? true) && !!name && name.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for creating gangs.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useCreateGang() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateGangRequest) => createGang(request),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gangKeys.lists() });
    },
  });
}

/**
 * Hook for updating gangs.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useUpdateGang() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateGangRequest) => updateGang(request),

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: gangKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: gangKeys.detail(variables.gangId),
      });
    },
  });
}

/**
 * Hook for deleting a gang.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useDeleteGang() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (gangId: string) => deleteGang(gangId),

    onSuccess: (_data, gangId) => {
      // Optimistically remove the deleted gang from the cached list
      queryClient.setQueriesData<ListGangsResponse['gangs']>(
        { queryKey: gangKeys.lists() },
        old => old?.filter(g => g.id !== gangId)
      );
      queryClient.removeQueries({ queryKey: gangKeys.details() });
      queryClient.invalidateQueries({ queryKey: gangKeys.lists() });
    },
  });
}

/**
 * Hook for listing gangs for the current organisation
 */
export function useListGangs() {
  return useQuery({
    queryKey: gangKeys.lists(),
    queryFn: async () => {
      const result = await listGangs();
      return result.gangs;
    },
    staleTime: 60_000,
  });
}
